import { useState, useEffect, useCallback, Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { fetchAPI } from '../../api/request';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import Loading from '../common/Loading.component';
import FilterControls from './components/FilterControls.component';
import { useTheme } from '@emotion/react';
import _ from 'lodash';
import {
  getApiPath,
  getRangeDateFilters,
  getTodayDateFilters,
  isValidDate,
} from '../../module/yachtDashboard/conf/Data.conf';
import DashboardContent from './components/DashboardContent.component';
import { useFormContext } from 'react-hook-form';
import NotesBox from './components/NotesBox.component';
import YachtCard from '../yacht/CardYacht.component';

const YachtDashboard = ({ yachtID, children, methods, initialState }) => {
  const theme = useTheme();
  const { getValues, reset } = useFormContext();
  const { jwt } = useSelector((state) => state.login);
  const [yacht, setYacht] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wallNote, setWallNote] = useState('');
  const [expandedCard, setExpandedCard] = useState(false);
  const [expandedNote, setExpandedNote] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(initialState);

  const formValues = useMemo(() => getValues(), [getValues]);
  const { start_date, end_date } = formValues;

  const buildFilterQuery = useCallback(
    (type, key, operator, value) =>
      `&populate[${type}][filters][${key}][${operator}]=${value}`,
    []
  );

  const fetchYachtData = useCallback(
    ({ filterDates = false, isToday = false, formValues = null } = {}) => {
      setLoading(true);

      const { start_date, end_date, id_department } = formValues ?? getValues();
      const departmentId = id_department?.id;

      let filters = '';
      let logbookFilter = '&[populate][id_logbooks]=%2A';

      if (id_department) {
        filters +=
          buildFilterQuery(
            'id_operation_lists',
            'id_department][id]',
            '$eq',
            departmentId
          ) +
          buildFilterQuery(
            'id_logbooks',
            'id_department][id]',
            '$eq',
            departmentId
          );
      }

      if (isToday) {
        const today = dayjs().format('YYYY-MM-DD');
        logbookFilter = buildFilterQuery(
          'id_logbooks',
          'collection_delivery_date',
          '$eq',
          today
        );

        const todayFilters = getTodayDateFilters(today);

        todayFilters.forEach(({ type, key, operator, date }) => {
          filters += buildFilterQuery(type, key, operator, date);
        });
      } else if (
        filterDates ||
        !_.isEmpty(start_date) ||
        !_.isEmpty(end_date)
      ) {
        const dateFilters = getRangeDateFilters(start_date, end_date);

        dateFilters.forEach(({ type, key, operator, date }) => {
          if (isValidDate(date)) {
            filters += buildFilterQuery(type, key, operator, date);
          }
        });
      }

      fetchAPI(
        {
          path: `${getApiPath(yachtID)}${filters}${logbookFilter}`,
          method: 'GET',
          auth: true,
        },
        {},
        '',
        {},
        jwt
      )
        .then((resp) => {
          setYacht(resp?.data?.attributes);
          setWallNote(resp?.data?.attributes?.wall || '');
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching yacht data:', error);
          setLoading(false);
        });
    },
    [jwt, yachtID, buildFilterQuery, getValues]
  );

  const onReset = useCallback(() => {
    reset(initialState);
    setAppliedFilters(initialState);
  }, [reset, initialState]);

  useEffect(() => {
    if (yachtID !== null) {
      fetchYachtData();
    }
    // eslint-disable-next-line
  }, [jwt, yachtID]);

  useEffect(() => {
    if (yachtID !== null && _.isEmpty(start_date) && _.isEmpty(end_date)) {
      fetchYachtData();
    }
    // eslint-disable-next-line
  }, [jwt, yachtID, start_date, end_date]);

  useEffect(() => {
    const today = dayjs().format('YYYY-MM-DD');
    if (start_date === today && end_date === today) {
      fetchYachtData();
    }
    // eslint-disable-next-line
  }, [start_date, end_date]);

  const applyDateFilter = useCallback(() => {
    const values = getValues();
    setAppliedFilters(values);
    fetchYachtData({ filterDates: true, formValues: values });
  }, [fetchYachtData, getValues]);

  const filterToday = useCallback(() => {
    const today = dayjs().format('YYYY-MM-DD');
    methods.setValue('start_date', today);
    methods.setValue('end_date', today);

    const values = {
      ...getValues(),
      start_date: today,
      end_date: today,
    };

    setAppliedFilters(values);
    fetchYachtData({ isToday: true, formValues: values });
  }, [methods, fetchYachtData, getValues]);

  return (
    <Box
      sx={{
        height: 'fit-content',
        backgroundColor: theme.palette.grey[100],
        borderRadius: 5,
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
        <FilterControls
          children={children}
          onApplyFilter={methods.handleSubmit(applyDateFilter)}
          onFilterToday={filterToday}
          onResetFilters={onReset}
        />
      </Box>
      {loading ? (
        <Loading />
      ) : (
        <Fragment>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 3,
            }}
          >
            <Box
              sx={{ width: { xs: '100%', lg: '24%' }, ml: { xs: 0, lg: 1 } }}
            >
              <YachtCard
                yachtData={yacht}
                yachtID={yachtID}
                expandedCard={expandedCard}
                setExpandedCard={setExpandedCard}
                methods={methods}
              />
            </Box>
            <Box
              sx={{
                width: { xs: '100%', lg: '76%' },
                mr: { xs: 0, lg: 1.5 },
                px: { xs: 0.5, lg: 0 },
              }}
            >
              <NotesBox
                yachtID={yachtID}
                jwt={jwt}
                initialNote={wallNote}
                expandedNote={expandedNote}
                setExpandedNote={setExpandedNote}
              />
            </Box>
          </Box>
          <DashboardContent
            jwt={jwt}
            yacht={yacht}
            yachtID={yachtID}
            activeTab={activeTab}
            filters={appliedFilters}
            onTabChange={(_, newValue) => setActiveTab(newValue)}
          />
        </Fragment>
      )}
    </Box>
  );
};

export default YachtDashboard;